import React from 'react';
import { COLORS } from '../../config/designSystem/Colors';
import { SPACING } from '../../config/designSystem/Spacing';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { keyOfFrame } from '../../types/types';
import { useBreakpointWidth } from '../../hooks/useBreakpointWidth';

interface Labels {
  [key: string]: string;
}

interface RadioButtonProps {
  label: string;
  sublabel: string;
  value: number;
  selected: boolean;
  onPress: (value: number) => void;
}

const RadioButton: React.FC<RadioButtonProps> = ({ label, sublabel, value, selected, onPress }) => {
  const circleSize = useBreakpointWidth({ sm: 28, md: 36 });
  const fontSize = useBreakpointWidth({ sm: 14, md: 16 });
  const sublabelSize = useBreakpointWidth({ sm: 10, md: 12 });

  return (
    <TouchableOpacity
      style={styles.radioContainer}
      onPress={() => onPress(value)}
    >
      <View style={[
        styles.radioCircle,
        selected && styles.radioSelected,
        { width: circleSize, height: circleSize, borderRadius: circleSize / 2 }
      ]}>
        <Text style={[
          styles.radioLabel,
          selected && styles.radioLabelSelected,
          { fontSize }
        ]}>
          {label}
        </Text>
      </View>
      <Text style={[
        styles.sublabel,
        selected && styles.sublabelSelected,
        { fontSize: sublabelSize }
      ]} numberOfLines={2}>
        {sublabel}
      </Text>
    </TouchableOpacity>
  );
};

interface RadioGroupProps {
  getValue: (key: keyOfFrame) => any;
  setValue: (key: keyOfFrame, value: any) => void;
  labels: Labels;
  nextAvailable: boolean;
  onNextCb: (...args: any[]) => void;
}

const RadioGroup: React.FC<RadioGroupProps> = ({ getValue, setValue, labels, nextAvailable, onNextCb }) => {
  const columnGap = useBreakpointWidth({ sm: SPACING['sp-8'], md: SPACING['sp-32'] });
  const padding = useBreakpointWidth({ sm: SPACING['sp-8'], md: SPACING['sp-16'] });

  const selectedValue = getValue('importance');
  const labelValues = [
    labels?.NOT_AT_ALL,
    labels?.A_LITTLE,
    labels?.SOMEWHAT,
    labels?.VERY,
    labels?.EXTREMELY
  ];

  const handlePress = (value: number) => {
    setValue('importance', value);
    const valueSonar = getValue('valueSonar');
    if(nextAvailable && !!valueSonar) {
      onNextCb && onNextCb({force: true});
    }
  };

  return (
    <View style={[styles.container, { columnGap, paddingHorizontal: padding }]}>
      {[1, 2, 3, 4, 5].map((value, index) => (
        <RadioButton
          key={value}
          label={value.toString()}
          sublabel={labelValues[index]}
          value={value}
          selected={selectedValue === value}
          onPress={handlePress}
        />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  radioContainer: {
    alignItems: 'center',
    padding: SPACING['sp-8'],
    maxWidth: 80
  },
  radioCircle: {
    borderWidth: 2,
    borderColor: COLORS.border.secondary,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
    marginBottom: SPACING['sp-8']
  },
  radioSelected: {
    backgroundColor: COLORS.brand[400],
    borderColor: COLORS.brand[400]
  },
  radioLabel: {
    textAlign: 'center',
    color: '#000000',
    fontWeight: '500'
  },
  radioLabelSelected: {
    color: '#FFFFFF'
  },
  sublabel: {
    textAlign: 'center',
    color: '#000000',
    marginTop: 4
  },
  sublabelSelected: {
    color: COLORS.brand[400]
  }
});

export default RadioGroup;
